import { useMemo } from "react";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Brush,
} from "recharts";

// Generic single-series recharts line chart for numeric metrics on their OWN
// auto-scaled Y axis (voltage / current / power / rssi — each has a very
// different range, so one shared axis would flatten the others). Presentational
// only: the caller passes already-downsampled rows. Missing samples ("-"/""/
// null) break the line (connectNulls=false) so a gap reads as a real outage.

interface Props {
  data: any[];
  dataKey: string; // row field to plot
  name: string; // legend/tooltip label (already translated)
  color: string;
  timeKey?: string; // default "time"
  unitSuffix?: string; // e.g. " V"
  height?: number;
  // Brush: to sync a single slider across several stacked charts, render it on
  // ONE chart (showBrush) with controlled start/end indices + onBrushChange,
  // and feed the other charts the already-sliced window via `data`.
  showBrush?: boolean; // render the zoom/scroll brush (default true)
  brushStartIndex?: number;
  brushEndIndex?: number;
  onBrushChange?: (range: { startIndex: number; endIndex: number }) => void;
}

const toMs = (v: any): number =>
  typeof v === "number" ? v : dayjs(v).valueOf();

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "-" || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

export default function ChartMetricLine({
  data,
  dataKey,
  name,
  color,
  timeKey = "time",
  unitSuffix = "",
  height = 240,
  showBrush = true,
  brushStartIndex,
  brushEndIndex,
  onBrushChange,
}: Props) {
  const rows = useMemo(
    () =>
      (data ?? []).map((r) => ({
        t: toMs(r?.[timeKey]),
        v: toNum(r?.[dataKey]),
      })),
    [data, dataKey, timeKey]
  );
  const multiDay = useMemo(() => {
    if (rows.length < 2) return false;
    return rows[rows.length - 1].t - rows[0].t > 24 * 60 * 60 * 1000;
  }, [rows]);
  const tickFmt = (t: number) =>
    multiDay ? dayjs(t).format("DD/MM HH:mm") : dayjs(t).format("HH:mm");

  return (
    <div>
      <div
        style={{
          textAlign: "center",
          fontSize: 13,
          fontWeight: 600,
          color: "#334155",
          margin: "0 0 6px",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 9,
            height: 9,
            borderRadius: 9,
            background: color,
            marginRight: 6,
          }}
        />
        {name}
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={rows} margin={{ top: 10, right: 24, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" />
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={tickFmt}
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            minTickGap={40}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            width={48}
            domain={["auto", "auto"]}
          />
          <Tooltip
            labelFormatter={(t) => dayjs(t as number).format("DD/MM/YYYY HH:mm")}
            formatter={(value: any) => [
              value === null || value === undefined
                ? "-"
                : `${value}${unitSuffix}`,
              name,
            ]}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            wrapperStyle={{ zIndex: 20 }}
          />
          <Line
            type="monotone"
            dataKey="v"
            name={name}
            stroke={color}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          {showBrush && rows.length > 30 ? (
            <Brush
              dataKey="t"
              height={20}
              stroke="#cbd5e1"
              tickFormatter={tickFmt}
              startIndex={brushStartIndex}
              endIndex={brushEndIndex}
              onChange={(e: any) =>
                onBrushChange?.({
                  startIndex: e?.startIndex ?? 0,
                  endIndex: e?.endIndex ?? rows.length - 1,
                })
              }
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
